import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import { eventBus } from '@/services/eventBus';
import type {
  CreateNoteInput,
  Note,
  NotesCollection,
  NotesStats,
  NotesUIState,
  UpdateNoteInput
} from '@/types/notes';
import {
  detectNoteIconFromText,
  extractNoteTags,
  normalizeNote,
  sortNotesForDisplay
} from '@/utils/notesHelpers';
import { createEntityCache } from '@/stores/_shared/entityStoreHelpers';
import { buildSearchIndex, searchIndex } from '@/utils/searchIndex';

type NotesStorageMap = Record<string, NotesCollection>;

const STORAGE_KEY = 'app_notes';
const STORAGE_VERSION = '1.0.0';

const generateNoteId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `note_${Math.random().toString(36).slice(2, 10)}`;
};

const ensureManuscriptId = (value: string | null): string => {
  if (!value) {
    throw new Error('Select or create a manuscript before managing notes.');
  }
  return value;
};

const cloneNote = (note: Note): Note => ({
  ...note,
  tags: [...note.tags]
});

const readStorage = (): NotesStorageMap => {
  if (typeof window === 'undefined') {
    return {};
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return {};
    }
    const parsed = JSON.parse(raw) as NotesStorageMap;
    if (!parsed || typeof parsed !== 'object') {
      return {};
    }
    return parsed;
  } catch (error) {
    console.error('Failed to read notes from storage.', error);
    return {};
  }
};

const writeStorage = (data: NotesStorageMap): void => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    console.error('Failed to persist notes to storage.', error);
  }
};

const createEmptyCollection = (manuscriptId: string): NotesCollection => ({
  manuscriptId,
  notes: [],
  version: STORAGE_VERSION,
  lastUpdated: new Date().toISOString()
});

const mergeTags = (base: string[], extra: string[]): string[] => {
  const merged = new Set<string>();
  base.forEach((tag) => merged.add(tag));
  extra.forEach((tag) => merged.add(tag));
  return Array.from(merged).sort((a, b) => a.localeCompare(b));
};

const shouldGenerateCompletedTimestamp = (
  nextCompleted: boolean,
  previousCompleted: boolean,
  previousTimestamp: string | undefined
): string | undefined => {
  if (nextCompleted) {
    return previousTimestamp ?? new Date().toISOString();
  }
  return undefined;
};

export const useNotesStore = defineStore('notes', () => {
const notes = ref<Note[]>([]);
const activeManuscriptId = ref<string | null>(null);
const isLoading = ref(false);
const errorMessage = ref<string | null>(null);
const uiState = ref<NotesUIState>({
  showCompleted: false,
  selectedTag: null,
  editingNoteId: null,
  searchTerm: ''
});
const cache = createEntityCache<Note[]>();

const notesSnapshot = computed<Note[]>(() => {
  const cached = cache.get('active');
  if (cached !== undefined) {
    return cached.map((note) => cloneNote(note));
  }
  const cloneList = notes.value.map((note) => cloneNote(note));
  cache.set('active', cloneList);
  return cloneList.map((note) => cloneNote(note));
});

const noteSearchIndex = computed(() =>
  buildSearchIndex(notesSnapshot.value, (note) => [note.text, note.tags.join(' ')])
);

const stats = computed<NotesStats>(() => {
  const total = notesSnapshot.value.length;
  const completed = notesSnapshot.value.filter((note) => note.isCompleted).length;
  const pinned = notesSnapshot.value.filter((note) => note.isPinned).length;
  const taggedNotes = notesSnapshot.value.filter((note) => note.tags.length > 0).length;
  const uniqueTags = Array.from(
    notesSnapshot.value.reduce<Set<string>>((accumulator, note) => {
      note.tags.forEach((tag) => accumulator.add(tag));
      return accumulator;
    }, new Set<string>())
  ).sort((a, b) => a.localeCompare(b));

  return {
    total,
    active: total - completed,
    completed,
    pinned,
    tagged: taggedNotes,
    uniqueTags
  };
});

const visibleNotes = computed<Note[]>(() => {
  const includeCompleted = uiState.value.showCompleted;
  const selectedTag = uiState.value.selectedTag;
  const searchTerm = uiState.value.searchTerm.trim();

  let collection = sortNotesForDisplay(
    searchTerm.length > 0 ? searchIndex(noteSearchIndex.value, searchTerm) : notesSnapshot.value
  );

  if (!includeCompleted) {
    collection = collection.filter((note) => !note.isCompleted);
  }

  if (selectedTag) {
    collection = collection.filter((note) => note.tags.includes(selectedTag));
  }

  return collection;
});

const hasNotes = computed(() => notesSnapshot.value.length > 0);

  const extractEntityReferences = (tags: string[]): Array<{ entityType: string; entityId: string }> => {
    return tags
      .map((tag) => tag.split(':'))
      .filter((parts): parts is [string, string] => parts.length === 2 && parts[0].length > 0 && parts[1].length > 0)
      .map(([entityType, entityId]) => ({
        entityType,
        entityId
      }));
  };

  const emitReferenceUpdates = (manuscriptId: string, previousTags: string[], nextTags: string[]): void => {
    const previousRefs = extractEntityReferences(previousTags);
    const nextRefs = extractEntityReferences(nextTags);
    const allRefs = new Map<string, { entityType: string; entityId: string }>();

    [...previousRefs, ...nextRefs].forEach((ref) => {
      const key = `${ref.entityType}:${ref.entityId}`;
      allRefs.set(key, ref);
    });

    allRefs.forEach(({ entityType, entityId }) => {
      eventBus.emit('entity:referencesUpdated', {
        manuscriptId,
        entityType,
        entityId
      });
    });
  };

  const loadFromStorage = (manuscriptId: string | null): void => {
    if (!manuscriptId) {
      notes.value = [];
      return;
    }

    isLoading.value = true;
    errorMessage.value = null;

    try {
      const storage = readStorage();
      const collection = storage[manuscriptId] ?? createEmptyCollection(manuscriptId);
      const normalized = collection.notes.map(normalizeNote);
      notes.value = sortNotesForDisplay(normalized);
    cache.invalidate('active');
    } catch (error) {
      console.error('Unable to load notes from storage.', error);
      errorMessage.value = 'Unable to load notes for the selected manuscript.';
      notes.value = [];
    } finally {
      isLoading.value = false;
    }
  };

  const persistToStorage = (): void => {
    const manuscriptId = activeManuscriptId.value;
    if (!manuscriptId) {
      return;
    }

    const storage = readStorage();
    const snapshot: NotesCollection = {
      manuscriptId,
      notes: notes.value.map(cloneNote),
      version: STORAGE_VERSION,
      lastUpdated: new Date().toISOString()
    };

    storage[manuscriptId] = snapshot;
  cache.invalidate('active');
    writeStorage(storage);
  };

  const setActiveManuscript = (manuscriptId: string | null): void => {
    activeManuscriptId.value = manuscriptId;
  };

  const createNote = (input: Omit<CreateNoteInput, 'manuscriptId'>): Note => {
    const manuscriptId = ensureManuscriptId(activeManuscriptId.value);
    const trimmedText = input.text.trim();
    if (trimmedText.length === 0) {
      throw new Error('Note text cannot be empty.');
    }

    const autoTags = extractNoteTags(trimmedText);
    const mergedTags = input.tags ? mergeTags(autoTags, input.tags) : autoTags;
    const icon = input.icon ?? detectNoteIconFromText(trimmedText);
    const isCompleted = input.isCompleted ?? false;
    const now = new Date().toISOString();

    const note: Note = {
      id: generateNoteId(),
      manuscriptId,
      text: trimmedText,
      icon,
      tags: mergedTags,
      isPinned: input.isPinned ?? false,
      isCompleted,
      createdAt: now,
      updatedAt: now,
      completedAt: isCompleted ? now : undefined
    };

    notes.value = sortNotesForDisplay([note, ...notes.value]);
    cache.invalidate('active');
    persistToStorage();

    eventBus.emit('notes:changed', {
      manuscriptId,
      noteId: note.id,
      tags: [...note.tags]
    });
    emitReferenceUpdates(manuscriptId, [], note.tags);

    return note;
  };

  const updateNote = (noteId: string, updates: UpdateNoteInput): Note => {
    const manuscriptId = ensureManuscriptId(activeManuscriptId.value);
    const index = notes.value.findIndex((note) => note.id === noteId);
    if (index === -1) {
      throw new Error('Note not found.');
    }

    const existing = notes.value[index];
    const nextText = updates.text !== undefined ? updates.text.trim() : existing.text;
    if (nextText.length === 0) {
      throw new Error('Note text cannot be empty.');
    }

    const autoTags = extractNoteTags(nextText);
    const mergedTags = updates.tags ? mergeTags(autoTags, updates.tags) : autoTags;
    const nextIcon = updates.icon ?? detectNoteIconFromText(nextText);
    const nextCompleted = updates.isCompleted ?? existing.isCompleted;
    const nextPinned = updates.isPinned ?? existing.isPinned;
    const updatedAt = new Date().toISOString();

    const updated: Note = {
      ...existing,
      text: nextText,
      icon: nextIcon,
      tags: mergedTags,
      isPinned: nextPinned,
      isCompleted: nextCompleted,
      updatedAt,
      completedAt: shouldGenerateCompletedTimestamp(nextCompleted, existing.isCompleted, existing.completedAt)
    };

    notes.value.splice(index, 1, updated);
    notes.value = sortNotesForDisplay(notes.value);
    cache.invalidate('active');
    persistToStorage();

    eventBus.emit('notes:changed', {
      manuscriptId,
      noteId,
      tags: [...updated.tags]
    });
    emitReferenceUpdates(manuscriptId, existing.tags, updated.tags);

    return updated;
  };

  const getNoteById = (noteId: string): Note | null => {
    return notes.value.find((note) => note.id === noteId) ?? null;
  };

  const deleteNote = (noteId: string): void => {
    const manuscriptId = ensureManuscriptId(activeManuscriptId.value);
    const index = notes.value.findIndex((note) => note.id === noteId);
    if (index === -1) {
      throw new Error('Note not found.');
    }
    const note = notes.value[index];
    notes.value.splice(index, 1);
    notes.value = sortNotesForDisplay(notes.value);
    cache.invalidate('active');
    persistToStorage();

    eventBus.emit('notes:changed', {
      manuscriptId,
      noteId,
      tags: []
    });
    emitReferenceUpdates(manuscriptId, note.tags, []);
  };

  const togglePin = (noteId: string): Note => {
    const existing = getNoteById(noteId);
    if (!existing) {
      throw new Error('Note not found.');
    }
    return updateNote(noteId, { isPinned: !existing.isPinned });
  };

  const toggleCompleted = (noteId: string): Note => {
    const existing = getNoteById(noteId);
    if (!existing) {
      throw new Error('Note not found.');
    }
    return updateNote(noteId, { isCompleted: !existing.isCompleted });
  };

  const clearState = (): void => {
    notes.value = [];
    uiState.value = {
      showCompleted: false,
      selectedTag: null,
      editingNoteId: null,
      searchTerm: ''
    };
    errorMessage.value = null;
  cache.invalidate('active');
  };

  const setShowCompleted = (value: boolean): void => {
    uiState.value.showCompleted = value;
  };

  const setSelectedTag = (tag: string | null): void => {
    uiState.value.selectedTag = tag;
  };

  const setSearchTerm = (value: string): void => {
    uiState.value.searchTerm = value;
  };

  const setEditingNoteId = (noteId: string | null): void => {
    uiState.value.editingNoteId = noteId;
  };

  const clearFilters = (): void => {
    uiState.value.showCompleted = false;
    uiState.value.selectedTag = null;
    uiState.value.searchTerm = '';
  };

  const exportNotes = (): string => {
    const manuscriptId = ensureManuscriptId(activeManuscriptId.value);
    const payload = {
      version: STORAGE_VERSION,
      exportedAt: new Date().toISOString(),
      manuscriptId,
      notes: notes.value.map(cloneNote)
    };
    return JSON.stringify(payload, null, 2);
  };

  const importNotes = (jsonData: string): number => {
    const manuscriptId = ensureManuscriptId(activeManuscriptId.value);
    let parsed: unknown;
    try {
      parsed = JSON.parse(jsonData);
    } catch (error) {
      throw new Error('Invalid notes import: unable to parse JSON.');
    }

    if (!parsed || typeof parsed !== 'object') {
      throw new Error('Invalid notes import: unexpected payload.');
    }

    const payload = parsed as Partial<NotesCollection> & {
      notes?: Partial<Note>[];
    };

    if (!Array.isArray(payload.notes)) {
      throw new Error('Invalid notes import: expected an array of notes.');
    }

    const now = new Date().toISOString();
    const importedNotes: Note[] = payload.notes
      .map((entry) => {
        const safeText = typeof entry.text === 'string' ? entry.text.trim() : '';
        if (safeText.length === 0) {
          return null;
        }
        const raw: Note = {
          id: typeof entry.id === 'string' && entry.id.trim().length > 0 ? entry.id : generateNoteId(),
          manuscriptId,
          text: safeText,
          icon: entry.icon ?? detectNoteIconFromText(safeText),
          tags: Array.isArray(entry.tags) ? entry.tags : [],
          isPinned: Boolean(entry.isPinned),
          isCompleted: Boolean(entry.isCompleted),
          createdAt: entry.createdAt ?? now,
          updatedAt: entry.updatedAt ?? now,
          completedAt: entry.isCompleted ? entry.completedAt ?? now : undefined
        };
        return normalizeNote(raw);
      })
      .filter((note): note is Note => note !== null);

    if (importedNotes.length === 0) {
      return 0;
    }

    const existingIds = new Set(notes.value.map((note) => note.id));
    const deduped = importedNotes.map((note) => {
      if (existingIds.has(note.id)) {
        return {
          ...note,
          id: generateNoteId()
        };
      }
      return note;
    });

    notes.value = sortNotesForDisplay([...notes.value, ...deduped]);
  cache.invalidate('active');
    persistToStorage();
    return deduped.length;
  };

  watch(
    activeManuscriptId,
    (manuscriptId) => {
      loadFromStorage(manuscriptId);
    },
    { immediate: true }
  );

  return {
    notes,
    visibleNotes,
    stats,
    hasNotes,
    isLoading,
    errorMessage,
    uiState,
    setActiveManuscript,
    createNote,
    updateNote,
    deleteNote,
    togglePin,
    toggleCompleted,
    clearState,
    clearFilters,
    getNoteById,
    setShowCompleted,
    setSelectedTag,
    setSearchTerm,
    setEditingNoteId,
    exportNotes,
    importNotes
  };
});

